// JS reserved words (ES5 dialect — matches `reserved-words` default). Inlined
// to avoid pulling the `reserved-words` package, which transitively drags the
// `assert` + `util` Node polyfills onto every client bundle that imports
// this file (~70KB on /login via the entity-validation graph).
// Lists: ECMA-262 5th ed. 7.6.1 (Keyword, FutureReservedWord, NullLiteral,
// BooleanLiteral).
const ES5_RESERVED_WORDS = new Set([
  'break',
  'case',
  'catch',
  'continue',
  'debugger',
  'default',
  'delete',
  'do',
  'else',
  'finally',
  'for',
  'function',
  'if',
  'in',
  'instanceof',
  'new',
  'return',
  'switch',
  'this',
  'throw',
  'try',
  'typeof',
  'var',
  'void',
  'while',
  'with',
  'class',
  'enum',
  'extends',
  'super',
  'const',
  'export',
  'import',
  'null',
  'true',
  'false',
]);

// SQL reserved words
export const sqlReservedWords = new Set(
  [
    'select',
    'from',
    'where',
    'insert',
    'update',
    'delete',
    'join',
    'create',
    'alter',
    'drop',
    'table',
    'index',
    'view',
    'procedure',
    'trigger',
    'function',
    'database',
    'schema',
    'grant',
    'revoke',
    'commit',
    'rollback',
    'savepoint',
    'transaction',
    'with',
    'as',
    'and',
    'or',
    'not',
    'between',
    'in',
    'like',
    'is',
    'null',
    'distinct',
    'group',
    'by',
    'having',
    'order',
    'asc',
    'desc',
    'limit',
    'offset',
    'union',
    'all',
    'intersect',
    'except',
    'case',
    'when',
    'then',
    'else',
    'end',
    'exists',
    'for',
    'some',
    'any',
    'values',
    'into',
    'on',
    'inner',
    'outer',
    'left',
    'right',
    'full',
    'cross',
    'join',
    'natural',
    'using',
  ].map((w) => w.toLowerCase()),
);

// Groovy & Java reserved words
export const groovyReservedWords = new Set(
  [
    'as',
    'assert',
    'break',
    'case',
    'catch',
    'class',
    'const',
    'continue',
    'def',
    'default',
    'do',
    'else',
    'enum',
    'extends',
    'false',
    'finally',
    'for',
    'goto',
    'if',
    'implements',
    'import',
    'in',
    'instanceof',
    'interface',
    'new',
    'null',
    'package',
    'return',
    'super',
    'switch',
    'this',
    'throw',
    'throws',
    'trait',
    'true',
    'try',
    'while',
    'abstract',
    'final',
    'native',
    'private',
    'protected',
    'public',
    'static',
    'strictfp',
    'synchronized',
    'transient',
    'volatile',
  ].map((w) => w.toLowerCase()),
);

// DOM globals
export const domGlobals = new Set(
  [
    'document',
    'window',
    'alert',
    'console',
    'localStorage',
    'sessionStorage',
    'navigator',
    'location',
    'history',
    'screen',
    'performance',
    'fetch',
    'XMLHttpRequest',
    'Event',
    'Node',
    'Element',
    'HTMLElement',
    'CSS',
    'CSSStyleDeclaration',
    'matchMedia',
    'crypto',
    'setTimeout',
    'clearTimeout',
    'setInterval',
    'clearInterval',
    'requestAnimationFrame',
    'cancelAnimationFrame',
    'addEventListener',
    'removeEventListener',
    'dispatchEvent',
  ].map((w) => w.toLowerCase()),
);

export const scopePathReservedWords = new Set(
  [
    // per-block / per-entry state members
    'value',
    'instances',
    'context',
    'item',
    // top-level compute-context keys
    'sessionStorage',
    'localStorage',
    'location',
    'utils',
    'pageInputs',
    'inputs',
    'lib',
    'pageParams',
    'userContext',
    'deviceContext',
    'interfaceRecord',
    'runtimeBlocks',
    'dataSources',
    'dataSourcesMeta',
    'walkthrough',
    'envVariables',
    'queryMeta',
    'mediaUploads',
    'theme',
    'actions',
    'appsAndIntegrations',
    'serverUtils',
  ].map((word) => word.toLowerCase()),
);

export const commonGlobalObjects = new Set(
  [
    'setTimeout',
    'clearTimeout',
    'setInterval',
    'clearInterval',
    'Promise',
    'Map',
    'Set',
    'WeakMap',
    'WeakSet',
    'Array',
    'Object',
    'String',
    'Number',
    'Boolean',
    'Date',
    'Math',
    'JSON',
    'Error',
    'RegExp',
    'Function',
    'Symbol',
    'Proxy',
    'Reflect',
    'Intl',
    'BigInt',
    'globalThis',
    'undefined',
    'NaN',
    'isNaN',
    'isFinite',
    'parseFloat',
    'parseInt',
    'encodeURI',
    'decodeURI',
    'encodeURIComponent',
    'decodeURIComponent',
    'eval',
    'Infinity',
  ].map((w) => w.toLowerCase()),
);

/**
 * Check if a word is a JavaScript reserved word (ES5 dialect).
 */
export function isJavaScriptReservedWord(word: string): boolean {
  return ES5_RESERVED_WORDS.has(word);
}

/**
 * Combine all reserved words check
 */
export function isReservedWord(word: string): boolean {
  // Check JavaScript reserved words
  if (isJavaScriptReservedWord(word)) {
    return true;
  }

  // Check other reserved words (case-insensitive)
  const lowerCaseWord = word.toLowerCase();
  return (
    sqlReservedWords.has(lowerCaseWord) ||
    groovyReservedWords.has(lowerCaseWord) ||
    domGlobals.has(lowerCaseWord) ||
    commonGlobalObjects.has(lowerCaseWord) ||
    scopePathReservedWords.has(lowerCaseWord)
  );
}
